import React, { useEffect, useMemo, useState } from 'react';
import { FieldIcon, IconSearch, IconChevron, IconLink, IconUnlink, IconLive, IconDatabase, IconMap, IconClose } from './icons';
import { cubesInUse } from './reportModel';

const FIELD_MIME = 'application/x-report-field';

/** Highlight the first occurrence of `term` in `text`. */
const Highlight = ({ text, term }) => {
  if (!term) return text;
  const i = text.toLowerCase().indexOf(term);
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + term.length)}</mark>{text.slice(i + term.length)}</>;
};

const kindLabel = (field) => {
  if (field.kind === 'measure') return field.aggType ? `Measure · ${field.aggType}` : 'Measure';
  if (field.kind === 'time') return 'Date';
  return `Dimension · ${field.type || 'string'}`;
};

/**
 * Left pane: every dataset's fields, grouped by how they relate to the
 * current selection. Fields that cannot be combined with the selection are
 * disabled with the reason. Click toggles a field; fields can also be dragged
 * onto the build shelves.
 */
const FieldBrowser = ({ catalogue, form, statusOf, onToggle, dragRef, onShowMap }) => {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const inUse = useMemo(() => cubesInUse(form, catalogue.fields), [form, catalogue]);
  const hasSelection = inUse.size > 0;

  // Datasets in use start open; with only a few datasets, all of them do.
  const [expanded, setExpanded] = useState(() => new Set(catalogue.cubes.length <= 3 ? catalogue.cubes.map((c) => c.name) : []));
  useEffect(() => {
    if (inUse.size === 0) return;
    setExpanded((prev) => {
      const missing = [...inUse].filter((c) => !prev.has(c));
      return missing.length ? new Set([...prev, ...missing]) : prev;
    });
  }, [inUse]);
  const toggleCube = (name) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const matches = (cube, field) => !term || `${cube.title} ${cube.name} ${field.label} ${field.name}`.toLowerCase().includes(term);

  const groups = useMemo(() => {
    const visible = catalogue.cubes
      .map((c) => ({ ...c, fieldsShown: [...c.measures, ...c.dimensions].filter((f) => matches(c, f)) }))
      .filter((c) => c.fieldsShown.length > 0);
    const usable = (c) => [...c.measures, ...c.dimensions].some((f) => statusOf(f).ok);
    if (!hasSelection) {
      return [
        { key: 'datasets', title: 'Datasets', cubes: visible.filter((c) => !c.isMetric) },
        { key: 'live', title: 'Live metric history', cubes: visible.filter((c) => c.isMetric) },
      ];
    }
    return [
      { key: 'in-use', title: 'In this report', cubes: visible.filter((c) => inUse.has(c.name)) },
      { key: 'related', title: 'Related', cubes: visible.filter((c) => !inUse.has(c.name) && usable(c)) },
      { key: 'unrelated', title: 'Not related to this report', cubes: visible.filter((c) => !inUse.has(c.name) && !usable(c)), muted: true },
    ];
  }, [catalogue, term, hasSelection, inUse, statusOf]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSelected = (f) => form.measures.includes(f.name) || form.dimensions.includes(f.name) || form.timeDimension === f.name;

  const renderField = (field) => {
    const status = statusOf(field);
    const selected = isSelected(field);
    return (
      <div key={field.name} className="rf-field-wrap" title={status.ok ? `${kindLabel(field)} · ${field.name}` : status.reason}>
        <button
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-label={field.label}
          disabled={!status.ok}
          className={`rf-field rf-field--${field.kind} ${selected ? 'rf-field--selected' : ''}`}
          draggable={status.ok}
          onDragStart={(e) => {
            dragRef.current = { field, from: 'browser' };
            e.dataTransfer.setData(FIELD_MIME, field.name);
            e.dataTransfer.setData('text/plain', field.name);
            e.dataTransfer.effectAllowed = 'copyMove';
          }}
          onDragEnd={() => { dragRef.current = null; }}
          onClick={() => onToggle(field)}
        >
          <span className="rf-field-icon"><FieldIcon field={field} size={14} /></span>
          <span className="rf-field-label"><Highlight text={field.label} term={term} /></span>
          {!status.ok && <IconUnlink size={13} className="rf-field-blocked" />}
          <span className="rf-field-check" aria-hidden="true" />
        </button>
      </div>
    );
  };

  const renderCube = (cube, muted) => {
    const open = !!term || expanded.has(cube.name);
    const measures = cube.fieldsShown.filter((f) => f.kind === 'measure');
    const dims = cube.fieldsShown.filter((f) => f.kind !== 'measure');
    const selectedCount = [...cube.measures, ...cube.dimensions].filter(isSelected).length;
    const joinText = cube.joins.length
      ? `Related to ${cube.joins.map((j) => catalogue.byCube.get(j.to)?.title || j.to).join(', ')}`
      : null;
    return (
      <section key={cube.name} className={`rf-cube ${muted ? 'rf-cube--muted' : ''}`} aria-label={cube.title}>
        <button type="button" className="rf-cube-header" aria-expanded={open} onClick={() => toggleCube(cube.name)}>
          <IconChevron size={14} className={`rf-chevron ${open ? 'rf-chevron--open' : ''}`} />
          <span className="rf-cube-icon">{cube.isMetric ? <IconLive size={14} /> : <IconDatabase size={14} />}</span>
          <span className="rf-cube-title"><Highlight text={cube.title} term={term} /></span>
          {selectedCount > 0 && <span className="rf-count">{selectedCount}</span>}
          {joinText && <span className="rf-cube-join" title={`${joinText}\n${cube.joins.map((j) => j.on).join('\n')}`}><IconLink size={13} /></span>}
        </button>
        {open && (
          <div className="rf-cube-body">
            {measures.length > 0 && <div className="rf-subhead">Measures</div>}
            {measures.map(renderField)}
            {dims.length > 0 && <div className="rf-subhead">Dimensions</div>}
            {dims.map(renderField)}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="rf-browser">
      <div className="rf-pane-head">
        <h3>Data</h3>
        <button type="button" className="rf-icon-btn" onClick={onShowMap} title="Relationship map" aria-label="Relationship map">
          <IconMap size={16} />
        </button>
      </div>
      <div className="rf-search">
        <IconSearch size={15} />
        <input
          type="search"
          aria-label="Search fields"
          placeholder="Search fields and datasets"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && <button type="button" className="rf-search-clear" aria-label="Clear search" onClick={() => setSearch('')}><IconClose size={13} /></button>}
      </div>
      <div className="rf-browser-scroll">
        {catalogue.cubes.length === 0 && <div className="rf-empty">No datasets are available for this company. Data appears here once a sync tool has pushed it.</div>}
        {term && groups.every((g) => g.cubes.length === 0) && <div className="rf-empty">No fields match “{search}”.</div>}
        {groups.filter((g) => g.cubes.length > 0).map((g) => (
          <div key={g.key} className={`rf-group ${g.muted ? 'rf-group--muted' : ''}`} role="group" aria-label={g.title}>
            <div className="rf-group-title">{g.title}<span>{g.cubes.length}</span></div>
            {g.cubes.map((c) => renderCube(c, g.muted))}
          </div>
        ))}
      </div>
      <div className="rf-legend" aria-hidden="true">
        <span><FieldIcon field={{ kind: 'measure' }} size={12} /> Measure</span>
        <span><FieldIcon field={{ kind: 'dimension' }} size={12} /> Dimension</span>
        <span><FieldIcon field={{ kind: 'time' }} size={12} /> Date</span>
      </div>
    </div>
  );
};

export { FIELD_MIME };
export default FieldBrowser;
